import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { throttle, map, get } from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';
import { VIDEO_TYPE, IMAGE_TYPE, OVERLAY_CLICK_EVENT, ANNOTATION_CLICK_EVENT } from '../../../MediaViewer/consts';
import { isTouchDevice, collaborationEventBus } from '../../../Utils';
import { selectAnnotationsHidden, selectTempAnnotations } from '../../selectors/annotations';
import { sendMixPanelEvent } from '../../../Utils/mixpanel';
import { SELECT_COMMENT } from '../../../Utils/mixpanel/mixpanelEvents';
import * as Selectors from '../../selectors';
import * as FilesSelectors from '../../selectors/files';
import * as CommentsSelectors from '../../selectors/comments';
import { annotationClicked, overlayClicked, toggleAnnotationsVisible } from './actios';

import './style.scss';

const SUPPORTED_OVERLAY_FORMATS = [VIDEO_TYPE, IMAGE_TYPE];
const ANNOTATION_WIDTH = 16;
const CURSOR_OFFSET_Y = 8;
const CURSOR_OFFSET_X = 3;
export const MARKERS_HEIGHT = 25;

export class MediaOverlay extends Component {
    constructor() {
        super();

        this.state = {
            isVisible: false,
            width: 0,
            height: 0,
            left: 0,
            top: 0,
            indicatorTop: 0,
            indicatorLeft: 0,
            isTouch: isTouchDevice()
        };

        this.previousResizeHandler = null;

        this.handleNewMedia = this.handleNewMedia.bind(this);
        this.setOverlayPosition = this.setOverlayPosition.bind(this);
        this.renderAnnotations = this.renderAnnotations.bind(this);
        this.annotationClicked = this.annotationClicked.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onResizeHandler = throttle(this.onResizeHandler.bind(this), 0);
        this.resizeObserver = new ResizeObserver(this.onResizeHandler);
        this.toggleFaddedAnnotations = this.toggleFaddedAnnotations.bind(this);
        this.updateIndicatorPosition = this.updateIndicatorPosition.bind(this);
    }

    componentDidMount() {
        const { media } = this.props;

        document.addEventListener('transitionend', this.onResizeHandler);
        window.addEventListener('resize', this.onResizeHandler);

        this.handleNewMedia(null, media);
    }

    componentDidUpdate(prevProps) {
        if (this.props.media !== prevProps.media) {
            this.handleNewMedia(prevProps.media, this.props.media);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('transitionend', this.onResizeHandler);
        window.removeEventListener('resize', this.onResizeHandler);
        const currentObservedElement = get(this, 'props.media.element');
        currentObservedElement && this.resizeObserver.unobserve(
            currentObservedElement
        );
    }

    onResizeHandler() {
        const { media } = this.props;
        this.setOverlayPosition(media);
    }

    toggleFaddedAnnotations(e) {
        e.stopPropagation();

        this.props.toggleAnnotationsVisible();
    }

    updateIndicatorPosition(e) {
        this.setState({
            indicatorLeft: e.clientX - CURSOR_OFFSET_X,
            indicatorTop: e.clientY - CURSOR_OFFSET_Y
        });
    }

    handleNewMedia(oldMedia, newMedia) {
        if (oldMedia && oldMedia !== newMedia) {
            this.resizeObserver.unobserve(oldMedia.element);
        }

        if (!newMedia || !SUPPORTED_OVERLAY_FORMATS.includes(newMedia.type)) { return; }

        this.resizeObserver.observe(newMedia.element);

        this.setOverlayPosition(newMedia);
    }

    setOverlayPosition(media) {
        if (!media) { return; }

        const pos = media.element.getBoundingClientRect();
        if (!pos) { return; }

        const height = (media.type === VIDEO_TYPE) ? (pos.height - MARKERS_HEIGHT) : pos.height;

        this.setState({
            height,
            isVisible: true,
            width: pos.width,
            left: pos.left,
            top: pos.top
        });
    }

    onClick(e) {
        const {
            isActive,
            overlayClicked
        } = this.props;

        const {
            isTouch
        } = this.state;

        if (!isActive || isTouch) { return; }

        collaborationEventBus.emit(OVERLAY_CLICK_EVENT);

        const pos = this.overlay.getBoundingClientRect(),
            result = {
                top: e.clientY - pos.top,
                left: e.clientX - pos.left,
                height: pos.height,
                width: pos.width
            },
            position = {
                yPercentage: (result.top / result.height * 100).toFixed(2),
                xPercentage: (result.left / result.width * 100).toFixed(2)
            };

        overlayClicked({
            type: 'Dot',
            ...position
        });
    }

    renderAnnotations(annotations) {
        const halfWidth = ANNOTATION_WIDTH / 2;
        if (!annotations.length) { return null; }

        return map(annotations, (annotation) => (
            <div className={classnames('annotation', { faded: !annotation.isCurrent }, { removable: annotation.isRemovable })}
                key={`${annotation.xPercentage}-${annotation.yPercentage}`}
                onClick={(e) => this.annotationClicked(e, annotation)}
                style={{ left: `calc(${annotation.xPercentage}% - ${halfWidth}px)`, top: `calc(${annotation.yPercentage}% - ${halfWidth}px)` }}/>
        ));
    }

    annotationClicked(e, annotation) {
        e.stopPropagation();

        if (annotation.commentId) {
            collaborationEventBus.emit(ANNOTATION_CLICK_EVENT, annotation);
        }

        this.props.annotationClicked(annotation);

        sendMixPanelEvent({
            eventName: SELECT_COMMENT,
            eventData: {
                'Opened Comment Source': 'Dots'
            }
        });
    }

    render() {
        const {
            media,
            isActive,
            isVisible,
            annotations,
            fadedAnnotations,
            annotationsHidden,
            hasTempAnnotation
        } = this.props;

        // Only videos and images have annotations.
        if (!media || !SUPPORTED_OVERLAY_FORMATS.includes(media.type)) { return null; }

        // Don't render in error state or not active collaboration state
        if (!isVisible) { return null; }

        const {
            isTouch,
            width,
            height,
            top,
            left,
            indicatorTop,
            indicatorLeft
        } = this.state;

        const style = { width, height, top, left };
        const indicatorClasses = classnames('indicator', { 'has-temp': hasTempAnnotation });
        const overlayClasses = classnames('media-overlay', { active: isActive }, { 'is-touch': isTouch });

        return (
            <div style={style}
                ref={(ref) => this.overlay = ref}
                className={overlayClasses}
                onClick={this.onClick}
                onMouseEnter={this.updateIndicatorPosition}
                onMouseMove={this.updateIndicatorPosition}>
                {this.renderAnnotations(annotations)}
                {!annotationsHidden && this.renderAnnotations(fadedAnnotations)}
                <div className={indicatorClasses}
                    style={{ top: indicatorTop, left: indicatorLeft }}/>
            </div>
        );
    }
}

MediaOverlay.defaultProps = {
    overlayClicked: () => null,
    annotationClicked: () => null,
    toggleAnnotationsVisible: () => null,
    annotations: [],
    fadedAnnotations: [],
    annotationsHidden: false,
    isVisible: true,
    isActive: false,
    hasTempAnnotation: false
};

MediaOverlay.propTypes = {
    media: PropTypes.shape({
        type: PropTypes.string.isRequired,
        element: PropTypes.object.isRequired,
        api: PropTypes.object.isRequired
    }),
    overlayClicked: PropTypes.func.isRequired,
    annotationClicked: PropTypes.func.isRequired,
    toggleAnnotationsVisible: PropTypes.func.isRequired,
    annotations: PropTypes.array.isRequired,
    fadedAnnotations: PropTypes.array,
    isVisible: PropTypes.bool,
    isActive: PropTypes.bool,
    annotationsHidden: PropTypes.bool,
    hasTempAnnotation: PropTypes.bool
};

const mapStateToProps = (state) => ({
    annotations: CommentsSelectors.getTempAnnotations(state),
    fadedAnnotations: CommentsSelectors.getCurrentAnnotations(state),
    annotationsHidden: selectAnnotationsHidden(state),
    hasTempAnnotation: !!selectTempAnnotations(state).length,
    isVisible: Selectors.isCollaborationDataReady(state),
    isActive: FilesSelectors.isActiveStatus(state) && !Selectors.isSellerGroupInPreRevisionMode(state)
});

export default connect(mapStateToProps, {
    annotationClicked,
    overlayClicked,
    toggleAnnotationsVisible
})(MediaOverlay);
